import { CommonModule } from '@angular/common';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  input,
} from '@angular/core';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputNumberModule } from 'primeng/inputnumber';
import { ShFormControl } from '../form-control/form-control.base';
import { primeSize } from '../models';

/**
 * Number input. `useGrouping` is off by default (ids and codes are numbers
 * too), and read-only it shows the lock icon like `sh-input`.
 *
 *   <sh-number [(value)]="maxUsers" [min]="0" suffix=" users" />
 */
@Component({
  selector: 'sh-number',
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    InputNumberModule,
    IconFieldModule,
    InputIconModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ShNumberComponent),
      multi: true,
    },
  ],
  host: { class: 'sh-control', '[class.sh-invalid]': 'isInvalid()' },
  template: `
    <p-iconfield>
      <p-inputnumber
        [ngModel]="value()"
        (ngModelChange)="commit($event)"
        [ngModelOptions]="{ standalone: true }"
        [inputId]="controlId()"
        [placeholder]="placeholder() | translate"
        [ariaLabel]="ariaLabel() | translate"
        [min]="min()"
        [max]="max()"
        [step]="step()"
        [useGrouping]="useGrouping()"
        [suffix]="suffix()"
        [minFractionDigits]="minFractionDigits()"
        [maxFractionDigits]="maxFractionDigits()"
        [readonly]="isReadonly()"
        [disabled]="isDisabled()"
        [size]="pSize()"
        [fluid]="true"
        (onBlur)="markTouched()"
      ></p-inputnumber>
      <p-inputicon
        *ngIf="isReadonly()"
        styleClass="fa-solid fa-lock"
      ></p-inputicon>
    </p-iconfield>
  `,
})
export class ShNumberComponent extends ShFormControl<number> {
  readonly min = input<number>();
  readonly max = input<number>();
  readonly step = input(1);
  readonly useGrouping = input(false, { transform: booleanAttribute });
  readonly suffix = input<string>();
  readonly minFractionDigits = input<number>();
  readonly maxFractionDigits = input<number>();

  protected readonly pSize = computed(
    () => primeSize(this.size()) as 'small' | 'large'
  );
}
